/**
 * Checks if the current device can make phone calls
 * This uses multiple detection methods to determine if the device supports calling
 * @returns {boolean} True if the device likely supports calling, false otherwise
 */
export function canMakePhoneCalls(): boolean {
  // Check if we're in a browser environment
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  // Method 1: Check for desktop browsers that typically DON'T support calling
  const isDesktopBrowser =
    /Windows NT|Macintosh|Linux x86|Linux i686|Linux x86_64/i.test(navigator.userAgent) &&
    !/iPad|Android/i.test(navigator.userAgent);

  // If we detect a desktop browser, return false immediately
  if (isDesktopBrowser) {
    return false;
  }

  // NEW: Explicitly detect iPads (including newer ones that report as Mac)
  const isIpad = (() => {
    // Detect iPadOS 13+ (desktop mode iPads)
    if (navigator.userAgent.includes('Macintosh') && 'ontouchend' in document) {
      return true;
    }
    // Classic iPad detection via user agent
    if (/iPad/.test(navigator.userAgent)) {
      return true;
    }
    return false;
  })();

  // no iPad model has ever supported traditional cellular calling (GSM/VoLTE) via its own SIM or eSIM like an iPhone.
  if (isIpad) {
    return false;
  }

  // Method 2: Check for mobile devices that typically support calling
  const isMobileDevice = /Android|webOS|iPhone|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );

  // Method 3: Check if device has telephone API
  const hasTelephoneAPI = 'telephone' in navigator;

  // Method 4: Check if we can detect tel: protocol handler
  let supportsTelProtocol = false;
  try {
    // Create a hidden hyperlink to test tel: protocol support
    const testLink = document.createElement('a');
    testLink.href = 'tel:+123456789';
    testLink.style.display = 'none';
    document.body.appendChild(testLink);

    // Some browsers provide information about protocol handlers
    supportsTelProtocol =
      typeof testLink.protocol !== 'undefined' && testLink.protocol.indexOf('tel') !== -1;

    // Clean up
    document.body.removeChild(testLink);
  } catch (e) {
    // Error handling - continue with other detection methods
    console.warn("Couldn't test tel: protocol:", e);
  }

  // NEW: Detect Android tablets (Android + NOT Mobile)
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isMobile = /Mobile/i.test(navigator.userAgent);
  const isAndroidTablet = isAndroid && !isMobile;

  if (isAndroidTablet && !supportsTelProtocol) {
    return false;
  }

  // Method 5: Check for PWA display mode standalone/fullscreen on mobile
  // (many standalone PWAs on mobile support calling)
  const isPWA =
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches;

  // Better approach: Detect desktop browsers more reliably using feature detection
  // Most desktop browsers don't have touch capability or report as mobile
  const isLikelyDesktop =
    !('ontouchstart' in window) && !isMobileDevice && !/iPad|Android/i.test(navigator.userAgent);

  // If it looks like a desktop and not explicitly detected as a tablet or mobile, return false
  if (isLikelyDesktop) {
    return false;
  }

  // Combine all methods - if any detection method indicates calling capability, return true
  return isMobileDevice || hasTelephoneAPI || supportsTelProtocol || (isPWA && isMobileDevice);
}
